import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Bot, Gavel, History, Lock, LockOpen, Pencil, Plus, Search, Settings2, ShieldAlert, ShieldCheck, Siren, Trash2, TriangleAlert, Undo2 } from "lucide-react";
import { botDelete, botGet, botPatch, botPost, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ChannelSelect } from "@/components/pickers";
import {
  ChannelMulti, Empty, FeatureGate, Field, LinesInput, Loading, MemberSearch, NumberInput, PageHeader, Panel, Pill, PillTabs,
  RoleMulti, SaveBar, SimpleSelect, StatTile, TextArea, ToggleRow, dangerBtn, formatDateTime, formatDuration, inputC, outlineBtn,
  primaryBtn, timeAgo, useFeature,
} from "@/components/kit";

const CASE = {
  warn: ["Avvertimento", "yellow"], timeout: ["Timeout", "yellow"], untimeout: ["Timeout rimosso", "green"],
  kick: ["Espulsione", "red"], ban: ["Ban", "red"], unban: ["Sban", "green"], note: ["Nota", "blue"],
};
const SOURCES = { panel: "Pannello", command: "Comando", automod: "AutoMod", threshold: "Soglia avvertimenti", antiraid: "Anti-raid", tempban: "Fine ban temporaneo" };

export default function Moderation() {
  const [tab, setTab] = useState("cases");
  return (
    <div className="space-y-6">
      <PageHeader icon={ShieldAlert} title="Moderazione" subtitle="Storico delle sanzioni, filtri automatici, protezione dai raid e blocco del server." />
      <StatusBar />
      <PillTabs active={tab} onChange={setTab} tabs={[
        { id: "cases", label: "Sanzioni", icon: History }, { id: "automod", label: "AutoMod", icon: Bot },
        { id: "antiraid", label: "Anti-raid", icon: Siren }, { id: "settings", label: "Impostazioni", icon: Settings2 },
      ]} />
      {tab === "cases" && <Cases />}
      {tab === "automod" && <Automod />}
      {tab === "antiraid" && <Antiraid />}
      {tab === "settings" && <ModSettings />}
    </div>
  );
}

function StatusBar() {
  const [stats, setStats] = useState(null);
  const [lock, setLock] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState("");
  const load = useCallback(() => {
    botGet("moderation/stats", { days: 30 }).then(setStats).catch(() => {});
    botGet("moderation/lockdown").then(setLock).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const n = (t) => stats?.byType?.[t] ?? 0;
  const toggle = async () => {
    try {
      if (lock.server) { const r = await botDelete("moderation/lockdown"); toast.success("Lockdown terminato", { description: `${r.unlocked} canali sbloccati.` }); }
      else { const r = await botPost("moderation/lockdown", { reason: reason || undefined }); toast.success("Lockdown attivo", { description: `${r.locked} canali bloccati.` }); }
      setConfirm(false); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile icon={History} value={stats?.total ?? "—"} label="Sanzioni (30 giorni)" />
        <StatTile icon={TriangleAlert} value={n("warn")} label={`Avvertimenti · ${n("timeout")} timeout`} tone="#FEE75C" />
        <StatTile icon={Gavel} value={n("ban") + n("kick")} label={`${n("ban")} ban · ${n("kick")} espulsioni`} tone="#F23F43" />
        <StatTile icon={Bot} value={stats?.bySource?.automod ?? 0} label="Interventi AutoMod" tone="#00F0FF" />
      </div>
      <div className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center ${lock?.server ? "border-[#F23F43]/40 bg-[#F23F43]/10" : "border-[#232838] bg-[#161923]"}`}>
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${lock?.server ? "bg-[#F23F43]/20 text-[#ff7a7d]" : "bg-[#5865F2]/15 text-[#8ea0ff]"}`}>
          {lock?.server ? <Lock className="h-5 w-5" /> : <LockOpen className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-white">{lock?.server ? "Server in lockdown" : "Lockdown non attivo"}</p>
          <p className="text-xs text-[#949BA4]">{lock?.server ? `Dal ${formatDateTime(lock.since)}${lock.reason ? ` · ${lock.reason}` : ""} · ${lock.channels.length} canali bloccati` : "In caso di emergenza blocca tutti i canali con un clic."}</p>
        </div>
        <Button onClick={() => setConfirm(true)} disabled={!lock} className={lock?.server ? primaryBtn : dangerBtn} data-testid="lockdown-toggle">
          {lock?.server ? <><LockOpen className="mr-2 h-4 w-4" />Termina lockdown</> : <><Lock className="mr-2 h-4 w-4" />Attiva lockdown</>}
        </Button>
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader>
            <DialogTitle className="text-white">{lock?.server ? "Terminare il lockdown?" : "Attivare il lockdown?"}</DialogTitle>
            <DialogDescription className="text-[#949BA4]">{lock?.server ? "Tutti i canali bloccati torneranno com'erano prima." : "Nessuno potrà scrivere in nessun canale finché non lo termini."}</DialogDescription>
          </DialogHeader>
          {!lock?.server && <Field label="Motivo (facoltativo)"><Input value={reason} onChange={(e) => setReason(e.target.value)} className={inputC} /></Field>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} className={outlineBtn}>Annulla</Button>
            <Button onClick={toggle} className={lock?.server ? primaryBtn : dangerBtn}>{lock?.server ? "Termina" : "Blocca tutto"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Cases() {
  const [filters, setFilters] = useState({ query: "", type: "", source: "" });
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(30);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    const q = { limit };
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") q[k] = v; });
    try { setData(await botGet("moderation/cases", q)); } catch (e) { toast.error(apiError(e)); setData({ total: 0, cases: [] }); }
  }, [filters, limit]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <Panel>
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#949BA4]" />
          <Input value={filters.query} onChange={(e) => setFilters({ ...filters, query: e.target.value })} placeholder="Cerca utente, motivo, moderatore o numero…" className={`${inputC} pl-9`} data-testid="cases-search" />
        </div>
        <SimpleSelect className="w-44" value={filters.type || "all"} onChange={(v) => setFilters({ ...filters, type: v })}
          options={[{ value: "all", label: "Tutti i tipi" }, ...Object.entries(CASE).map(([k, v]) => ({ value: k, label: v[0] }))]} />
        <SimpleSelect className="w-48" value={filters.source || "all"} onChange={(v) => setFilters({ ...filters, source: v })}
          options={[{ value: "all", label: "Tutte le origini" }, ...Object.entries(SOURCES).map(([k, v]) => ({ value: k, label: v }))]} />
        <Button onClick={() => setCreating(true)} className={primaryBtn} data-testid="new-case-btn"><Plus className="mr-2 h-4 w-4" />Nuova sanzione</Button>
      </div>
      {!data ? <Loading /> : data.cases.length === 0 ? <Empty icon={ShieldCheck} title="Nessuna sanzione">Qui compariranno avvertimenti, timeout, espulsioni e ban.</Empty> : (
        <div className="divide-y divide-[#232838] overflow-hidden rounded-xl border border-[#232838]">
          {data.cases.map((c) => (
            <div key={c.id} className={`flex items-start gap-3 bg-[#131620] px-4 py-3 transition-colors hover:bg-[#1a1e2b] ${c.type === "warn" && !c.active ? "opacity-50" : ""}`}>
              <span className="mt-0.5 w-10 font-mono text-xs text-[#5f6479]">#{c.id}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone={CASE[c.type]?.[1]}>{CASE[c.type]?.[0] ?? c.type}</Pill>
                  <span className="font-semibold text-white">{c.userTag}</span>
                  {c.durationMs && <Pill>{formatDuration(c.durationMs)}</Pill>}
                  {c.type === "warn" && !c.active && <Pill>annullato</Pill>}
                </div>
                <p className="mt-1 text-sm text-[#DBDEE1]">{c.reason || <span className="text-[#5f6479]">Nessun motivo</span>}</p>
                <p className="mt-0.5 text-[11px] text-[#949BA4]">{c.moderatorTag} · {SOURCES[c.source] ?? c.source} · {timeAgo(c.createdAt)}</p>
              </div>
              <div className="flex gap-1">
                {c.type === "warn" && <Button size="icon" variant="ghost" title={c.active ? "Annulla avvertimento" : "Ripristina"} onClick={() => act(() => botPatch(`moderation/cases/${c.id}`, { active: !c.active }), c.active ? "Avvertimento annullato" : "Avvertimento ripristinato")} className="text-[#949BA4] hover:bg-white/5 hover:text-white"><Undo2 className="h-4 w-4" /></Button>}
                <Button size="icon" variant="ghost" title="Modifica motivo" onClick={() => setEditing(c)} className="text-[#949BA4] hover:bg-white/5 hover:text-white"><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" title="Elimina caso" onClick={() => act(() => botDelete(`moderation/cases/${c.id}`), "Caso eliminato")} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {data && data.total > data.cases.length && <div className="mt-4 text-center"><Button variant="outline" onClick={() => setLimit((l) => l + 30)} className={outlineBtn}>Mostra altri</Button></div>}
      <EditReason item={editing} onClose={() => setEditing(null)} onSaved={load} />
      <NewCase open={creating} onClose={() => setCreating(false)} onDone={load} />
    </Panel>
  );
}

function EditReason({ item, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  useEffect(() => setReason(item?.reason ?? ""), [item]);
  const save = async () => {
    try { await botPatch(`moderation/cases/${item.id}`, { reason: reason.trim() || null }); toast.success("Motivo aggiornato"); onSaved(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
        <DialogHeader><DialogTitle className="text-white">Caso #{item?.id}</DialogTitle><DialogDescription className="text-[#949BA4]">Modifica il motivo della sanzione.</DialogDescription></DialogHeader>
        <TextArea value={reason} onChange={setReason} rows={3} />
        <DialogFooter><Button onClick={save} className={primaryBtn}>Salva</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TIMEOUTS = [[10, "10 minuti"], [60, "1 ora"], [360, "6 ore"], [1440, "1 giorno"], [10080, "1 settimana"], [40320, "28 giorni"]];
const BANS = [[0, "Permanente"], [60, "1 ora"], [1440, "1 giorno"], [10080, "1 settimana"], [43200, "30 giorni"]];

export function NewCase({ open, onClose, onDone, member: fixed, initialType = "warn" }) {
  const [member, setMember] = useState(null);
  const [type, setType] = useState(initialType);
  const [reason, setReason] = useState("");
  const [minutes, setMinutes] = useState(60);
  const [banMinutes, setBanMinutes] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setType(initialType); setReason(""); setMember(fixed ?? null); } }, [open, initialType, fixed]);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { userId: member.id, reason: reason.trim() || undefined };
      const r = type === "timeout" ? await botPost("moderation/timeout", { ...body, minutes })
        : type === "ban" ? await botPost("moderation/ban", { ...body, durationMinutes: banMinutes || null })
        : await botPost(`moderation/${type}`, body);
      const c = r.case ?? r;
      toast.success(`${CASE[type][0]} registrato`, { description: `Caso #${c.id} · ${member.displayName}` });
      if (r.escalation?.case) toast.info("Sanzione automatica", { description: `Raggiunti ${r.activeWarnings} avvertimenti: ${CASE[r.escalation.action]?.[0].toLowerCase()} applicato.` });
      onDone?.(r); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const TYPES = [["warn", "Avvertimento", TriangleAlert], ["timeout", "Timeout", ShieldAlert], ["kick", "Espulsione", Gavel], ["ban", "Ban", Ban], ["note", "Nota", Pencil]];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
        <DialogHeader><DialogTitle className="text-white">Nuova sanzione</DialogTitle><DialogDescription className="text-[#949BA4]">Se attivo, il membro riceve un messaggio privato con il motivo.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {!fixed && <Field label="Membro"><MemberSearch value={member} onChange={setMember} /></Field>}
          <div className="flex flex-wrap gap-1.5">
            {TYPES.map(([t, l, I]) => (
              <button key={t} onClick={() => setType(t)} className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${type === t ? "border-[#5865F2] bg-[#5865F2]/20 text-white" : "border-[#232838] text-[#949BA4] hover:text-white"}`}><I className="h-3.5 w-3.5" />{l}</button>
            ))}
          </div>
          {type === "timeout" && <Field label="Durata"><SimpleSelect value={minutes} onChange={(v) => setMinutes(Number(v))} options={TIMEOUTS.map(([v, l]) => ({ value: v, label: l }))} /></Field>}
          {type === "ban" && <Field label="Durata"><SimpleSelect value={banMinutes} onChange={(v) => setBanMinutes(Number(v))} options={BANS.map(([v, l]) => ({ value: v, label: l }))} /></Field>}
          <Field label={type === "note" ? "Nota (visibile solo allo staff)" : "Motivo"}><TextArea value={reason} onChange={setReason} rows={3} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className={outlineBtn}>Annulla</Button>
          <Button onClick={submit} disabled={busy || !member || (type === "note" && !reason.trim())} className={type === "ban" || type === "kick" ? dangerBtn : primaryBtn} data-testid="new-case-confirm">Conferma</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTIONS = [["delete", "Solo elimina il messaggio"], ["warn", "Elimina + avvertimento"], ["timeout", "Elimina + timeout"], ["kick", "Elimina + espulsione"], ["ban", "Elimina + ban"]];
const HELP = {
  badWords: "Blocca le parole della lista (anche con trucchi tipo 3→e). Con * blocchi le varianti: stupid* = stupido, stupida…",
  invites: "Blocca i link di invito ad altri server Discord.", links: "Blocca i link tranne quelli dei domini consentiti.",
  caps: "Blocca i messaggi quasi tutti in MAIUSCOLO.", mentions: "Blocca chi menziona troppe persone o ruoli.",
  emojis: "Blocca i messaggi con troppe emoji.", spam: "Blocca chi manda troppi messaggi in pochi secondi.",
  duplicates: "Blocca chi ripete lo stesso messaggio.", newlines: "Blocca i muri di testo con troppe righe.", zalgo: "Blocca il testo con caratteri sovrapposti.",
};

function RuleParams({ id, rule, set }) {
  if (id === "badWords") return <Field label={`Parole vietate (${rule.words.length})`} hint="Una per riga."><LinesInput value={rule.words} onChange={(words) => set({ words })} placeholder={"parola1\nparola2\nvariant*"} /></Field>;
  if (id === "links") return <Field label="Domini consentiti" hint="Uno per riga (vale anche per i sottodomini)."><LinesInput rows={3} value={rule.allowedDomains} onChange={(allowedDomains) => set({ allowedDomains })} /></Field>;
  if (id === "caps") return <div className="flex flex-wrap gap-4"><Field label="Maiuscole oltre il"><NumberInput value={rule.percent} min={1} max={100} suffix="%" onChange={(percent) => set({ percent })} /></Field><Field label="Da messaggi lunghi"><NumberInput value={rule.minLength} min={1} max={2000} suffix="lettere" onChange={(minLength) => set({ minLength })} /></Field></div>;
  if (["mentions", "emojis", "newlines"].includes(id)) return <Field label="Massimo consentito"><NumberInput value={rule.max} min={1} max={200} onChange={(max) => set({ max })} /></Field>;
  if (id === "spam") return <div className="flex flex-wrap gap-4"><Field label="Più di"><NumberInput value={rule.messages} min={2} max={50} suffix="messaggi" onChange={(messages) => set({ messages })} /></Field><Field label="In"><NumberInput value={rule.seconds} min={1} max={120} suffix="secondi" onChange={(seconds) => set({ seconds })} /></Field></div>;
  if (id === "duplicates") return <div className="flex flex-wrap gap-4"><Field label="Stesso messaggio"><NumberInput value={rule.count} min={2} max={20} suffix="volte" onChange={(count) => set({ count })} /></Field><Field label="In"><NumberInput value={rule.seconds} min={5} max={600} suffix="secondi" onChange={(seconds) => set({ seconds })} /></Field></div>;
  return null;
}

function Automod() {
  const { meta } = useData();
  const f = useFeature("automod");
  return (
    <FeatureGate feature={f}>
      {(v) => {
        const setRule = (id, patch) => f.change({ rules: { ...v.rules, [id]: { ...v.rules[id], ...patch } } });
        const labels = meta?.automodRules || {};
        return (
          <div className="space-y-4">
            <ToggleRow title={v.enabled ? "AutoMod attivo" : "AutoMod disattivato"} desc="Il bot controlla ogni messaggio e interviene da solo." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} testId="automod-toggle" />
            <Panel title="Impostazioni generali">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Canale dove registrare gli interventi"><ChannelSelect value={v.logChannelId} onChange={(logChannelId) => f.change({ logChannelId })} /></Field>
                <div className="space-y-2">
                  <ToggleRow title="Avvisa nel canale" desc="Messaggio temporaneo con il motivo." checked={v.notify} onChange={(notify) => f.change({ notify })} />
                  <ToggleRow title="Ignora lo staff" desc="Admin e chi gestisce i messaggi." checked={v.ignoreStaff} onChange={(ignoreStaff) => f.change({ ignoreStaff })} />
                </div>
                <Field label="Ruoli esclusi"><RoleMulti value={v.exemptRoleIds} onChange={(exemptRoleIds) => f.change({ exemptRoleIds })} /></Field>
                <Field label="Canali esclusi"><ChannelMulti value={v.exemptChannelIds} onChange={(exemptChannelIds) => f.change({ exemptChannelIds })} /></Field>
              </div>
            </Panel>
            <div className="grid gap-4 lg:grid-cols-2">
              {Object.entries(v.rules).map(([id, rule]) => (
                <Panel key={id} title={labels[id] ?? id} desc={HELP[id]} className={rule.enabled ? "" : "opacity-75"} right={<Switch checked={rule.enabled} onCheckedChange={(enabled) => setRule(id, { enabled })} data-testid={`rule-${id}`} />}>
                  {rule.enabled && (
                    <div className="space-y-3 border-t border-[#232838] pt-4">
                      <RuleParams id={id} rule={rule} set={(patch) => setRule(id, patch)} />
                      <div className="flex flex-wrap gap-4">
                        <Field label="Cosa fa il bot"><SimpleSelect className="w-56" value={rule.action} onChange={(action) => setRule(id, { action })} options={ACTIONS.map(([a, l]) => ({ value: a, label: l }))} /></Field>
                        {rule.action === "timeout" && <Field label="Durata timeout"><NumberInput value={rule.timeoutMinutes} min={1} max={40320} suffix="minuti" onChange={(timeoutMinutes) => setRule(id, { timeoutMinutes })} /></Field>}
                      </div>
                    </div>
                  )}
                </Panel>
              ))}
            </div>
            <SaveBar feature={f} />
          </div>
        );
      }}
    </FeatureGate>
  );
}

const PUNISH = [["none", "Nessuna azione"], ["timeout", "Timeout"], ["kick", "Espulsione"], ["ban", "Ban"]].map(([value, label]) => ({ value, label }));

function Antiraid() {
  const f = useFeature("antiraid");
  return (
    <FeatureGate feature={f}>
      {(v) => (
        <div className="space-y-4">
          <ToggleRow title={v.enabled ? "Anti-raid attivo" : "Anti-raid disattivato"} desc="Protegge il server quando entrano molti account insieme." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} />
          <Panel title="Quando scatta" icon={Siren}>
            <div className="flex flex-wrap items-center gap-2 text-sm text-[#DBDEE1]">
              Se entrano <NumberInput value={v.joins} min={2} max={100} className="w-20" onChange={(joins) => f.change({ joins })} /> persone in
              <NumberInput value={v.seconds} min={2} max={300} className="w-20" suffix="secondi," onChange={(seconds) => f.change({ seconds })} />
              la modalità raid resta attiva per <NumberInput value={v.raidMinutes} min={1} max={1440} className="w-20" suffix="minuti" onChange={(raidMinutes) => f.change({ raidMinutes })} />
            </div>
          </Panel>
          <Panel title="Durante il raid">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Chi entra durante il raid"><SimpleSelect value={v.raidAction} onChange={(raidAction) => f.change({ raidAction })} options={PUNISH} /></Field>
              <Field label="Durata timeout"><NumberInput value={v.timeoutMinutes} min={1} max={40320} suffix="minuti" onChange={(timeoutMinutes) => f.change({ timeoutMinutes })} /></Field>
              <ToggleRow title="Blocca tutti i canali" desc="Lockdown automatico per la durata del raid." checked={v.lockdown} onChange={(lockdown) => f.change({ lockdown })} />
              <ToggleRow title="Alza il livello di verifica" desc="E poi lo rimette com'era." checked={v.raiseVerification} onChange={(raiseVerification) => f.change({ raiseVerification })} />
            </div>
          </Panel>
          <Panel title="Account appena creati">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Età minima dell'account" hint="0 = disattivato. Vale sempre, anche senza raid."><NumberInput value={v.minAccountAgeDays} min={0} max={365} suffix="giorni" onChange={(minAccountAgeDays) => f.change({ minAccountAgeDays })} /></Field>
              <Field label="Azione sugli account troppo giovani"><SimpleSelect value={v.newAccountAction} onChange={(newAccountAction) => f.change({ newAccountAction })} options={PUNISH} /></Field>
            </div>
          </Panel>
          <SaveBar feature={f} />
        </div>
      )}
    </FeatureGate>
  );
}

function ModSettings() {
  const f = useFeature("moderation");
  return (
    <FeatureGate feature={f}>
      {(v) => {
        const setStep = (i, patch) => f.change({ warnThresholds: v.warnThresholds.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
        return (
          <div className="space-y-4">
            <Panel title="Generali" icon={Settings2}>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Canale mod-log" hint="Ogni sanzione viene registrata qui con un embed."><ChannelSelect value={v.logChannelId} onChange={(logChannelId) => f.change({ logChannelId })} /></Field>
                <Field label="Gli avvertimenti scadono dopo" hint="0 = non scadono mai."><NumberInput value={v.warnExpiryDays} min={0} max={3650} suffix="giorni" onChange={(warnExpiryDays) => f.change({ warnExpiryDays })} /></Field>
              </div>
              <div className="mt-4"><ToggleRow title="Messaggio privato al membro sanzionato" desc="Gli spiega cosa è successo e perché." checked={v.dmOnAction} onChange={(dmOnAction) => f.change({ dmOnAction })} /></div>
            </Panel>
            <Panel title="Sanzioni automatiche per avvertimenti" desc="Al raggiungimento di N avvertimenti attivi il bot applica la sanzione."
              right={<Button size="sm" onClick={() => f.change({ warnThresholds: [...v.warnThresholds, { count: (v.warnThresholds.at(-1)?.count ?? 0) + 2, action: "timeout", durationMinutes: 60 }] })} className={primaryBtn}><Plus className="mr-1 h-4 w-4" />Soglia</Button>}>
              <div className="space-y-2">
                {v.warnThresholds.map((t, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl border border-[#232838] bg-[#131620] p-3 text-sm text-[#DBDEE1]">
                    A <NumberInput value={t.count} min={1} max={100} className="w-20" suffix="avvertimenti →" onChange={(count) => setStep(i, { count })} />
                    <SimpleSelect className="w-36" value={t.action} onChange={(action) => setStep(i, { action })} options={[{ value: "timeout", label: "Timeout" }, { value: "kick", label: "Espulsione" }, { value: "ban", label: "Ban" }]} />
                    {t.action !== "kick" && <>per <NumberInput value={t.durationMinutes} min={0} max={525600} className="w-24" suffix={t.action === "ban" ? "min (0 = sempre)" : "minuti"} onChange={(durationMinutes) => setStep(i, { durationMinutes })} /></>}
                    <Button size="icon" variant="ghost" onClick={() => f.change({ warnThresholds: v.warnThresholds.filter((_, j) => j !== i) })} className="ml-auto text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                {v.warnThresholds.length === 0 && <p className="text-sm text-[#949BA4]">Nessuna soglia.</p>}
              </div>
            </Panel>
            <SaveBar feature={f} />
          </div>
        );
      }}
    </FeatureGate>
  );
}
